import { injectable } from "inversify";
import { OfflineService } from "./offline.service";
import { syncLog } from "@archipad/logs/logger";
import { combineLatest, filter, interval, Observable, Subscription, switchMap } from "rxjs";
import { ProjectEntity, VisitEntity, BugAnnotationEntity, RemarkAnnotationEntity } from "@archipad-js/archipad-model";
import { AccountInfo } from "./account.service.types";
import { DIID } from "@archipad-js/dependency-injection";
import { DAY_IN_MS, SECOND_IN_MS } from "@archipad-js/core/utils";


export type AttachmentCache = {
    userId: string;
    projectId: string;
    entityId: string;
    entityType: string;
    attachmentKey: string;
    attachmentData: Blob;
    cacheAt: Date;
}
const UPLOAD_TASK_POLLING_INTERVAL = 10 * SECOND_IN_MS;
const STALE_ATTACHMENT_CACHE_DURATION = 30 * DAY_IN_MS;
export const SUPPORT_ASYNC_ATTACHMENT_ENTITY = [
    VisitEntity,
    BugAnnotationEntity,
    RemarkAnnotationEntity,
];

export const AttachmentStoreService_DIID = DIID<AttachmentStoreServiceInterface>("AttachmentStoreService");
@injectable()
export abstract class AttachmentStoreServiceInterface {
    abstract getAttachmentCache({ userId, projectId, entityId, attachmentKey }: Pick<AttachmentCache, 'userId' | 'projectId' | 'entityId' | 'attachmentKey'>): Promise<AttachmentCache | undefined>;
    abstract setAttachmentCache(attachmentCache: AttachmentCache): Promise<void>;
    abstract deleteAttachmentCache({ userId, projectId, entityId, attachmentKey }: Pick<AttachmentCache, 'userId' | 'projectId' | 'entityId' | 'attachmentKey'> ): Promise<void>;
    abstract clearAttachmentCache({ userId, projectId }: Pick<AttachmentCache, 'userId' | 'projectId'>): Promise<void>;
    abstract clearAllAttachmentCache(): Promise<void>;

    abstract listAttachments({ userId, projectId }: Pick<AttachmentCache, 'userId' | 'projectId'>): Promise<AttachmentCache[]>;

    public getAttachmentCacheKey({ userId, projectId, entityId, attachmentKey }:  Pick<AttachmentCache, 'userId' | 'projectId' | 'entityId' | 'attachmentKey'>): `${string}-${string}-${string}-${string}` {
        if (!userId || !projectId ) {
            throw new Error("userId and projectId are required to generate attachment cache key.");
        }
        return `${userId}-${projectId}-${entityId}-${attachmentKey}`;
    }
}

export const AsyncUploadAttachmentService_DIID = DIID<AsyncUploadAttachmentService>("AsyncUploadAttachmentService");

@injectable()
export class AsyncUploadAttachmentService {

    protected subscription: Subscription | undefined;

    constructor(
        protected readonly offlineService: OfflineService,
        protected readonly attachmentStoreService: AttachmentStoreServiceInterface,
        protected readonly currentAccount$: Observable<AccountInfo>,
    ) {

    }

    /**
     * Rename (On XXX)
     */
    public triggerTaskPolling({
        projectEntity,
        uploadFileFn,
    }: {
        projectEntity: ProjectEntity;
        uploadFileFn: ({ file, attachmentKey, entityId, entityType }: {
            file: File;
            attachmentKey: string;
            entityId: string;
            entityType: string;
        } ) => Promise<void>,
    }): void {
        const taskPolling$ = combineLatest(
            [
                this.offlineService.onlineStatus$,
                this.currentAccount$,
                interval(UPLOAD_TASK_POLLING_INTERVAL),
            ],
        ).pipe(
            filter(([_,accountInfo]) => this.offlineService.isOnline() && !!accountInfo),
            switchMap(([isOnline, currentAccount]) => {
                return this.handleUploadAttachmentTask({
                    projectEntity,
                    currentAccount: currentAccount,
                    uploadFileFn,
                });
            }),
        );

        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = taskPolling$.subscribe();
    }

    public async garbageCollectAttachmentCache({
        userId,
        projectId,
    }: {
        userId: string;
        projectId: string;
    }): Promise<void> {
        const attachments = await this.attachmentStoreService.listAttachments({ userId, projectId });
        for (const attachment of attachments) {
            const isStale = new Date().getTime() - attachment.cacheAt.getTime() > STALE_ATTACHMENT_CACHE_DURATION;
            if (isStale) {
                await this.attachmentStoreService.deleteAttachmentCache({
                    userId: attachment.userId,
                    projectId: attachment.projectId,
                    entityId: attachment.entityId,
                    attachmentKey: attachment.attachmentKey,
                });
            }
        }
    }

    public async handleUploadAttachmentTask({
        projectEntity,
        currentAccount,
        uploadFileFn,
    }: {
        projectEntity: ProjectEntity,
        currentAccount: AccountInfo,
        uploadFileFn: ({ file, attachmentKey, entityId, entityType }: {
            file: File;
            attachmentKey: string;
            entityId: string;
            entityType: string;
        } ) => Promise<void>,
    }): Promise<void> {
        const userId = currentAccount.id?.toString();
        const projectId = projectEntity.id;
        const attachmentsCache = await this.attachmentStoreService.listAttachments({ userId, projectId });

        for (const attachmentCache of attachmentsCache) {
            const { entityId, attachmentKey, attachmentData, entityType } = attachmentCache;

            const entityWithAttachment = projectEntity.getContext().getEntity(entityType, entityId);
            if (!entityWithAttachment || entityWithAttachment.isDeleted()) {
                await this.attachmentStoreService.deleteAttachmentCache({
                    userId: userId,
                    projectId: projectId,
                    entityId: entityId,
                    attachmentKey: attachmentKey,
                });
                continue;
            }
            const file = new File([attachmentData], attachmentKey, { type: attachmentData.type });
            const attachmentLog = syncLog.child().withContext({ 'archipad.project.id': projectId, 'archipad.entity.id': entityId, 'archipad.attachment.key': attachmentKey });
            try {
                await uploadFileFn({
                    file: file,
                    attachmentKey: attachmentKey,
                    entityId: entityId,
                    entityType: entityType,
                });
                await this.attachmentStoreService.deleteAttachmentCache({
                    userId: userId,
                    projectId: projectId,
                    entityId: entityId,
                    attachmentKey: attachmentKey,
                });
                attachmentLog.info('sync.offline_attachment.uploaded');
            } catch (error) {
                console.error(`Failed to upload attachment for entity ${entityId} in project ${projectId}:`, error);
                attachmentLog.withError(error).error('sync.offline_attachment.upload_failed');
            }
        }
    }

    public async postUploadAttachmentTask({
        userId,
        projectId,
        entityId,
        entityType,
        attachmentKey,
        attachmentData,
    }: {
        userId: string;
        projectId: string;
        entityId: string;
        entityType: string;
        attachmentKey: string;
        attachmentData: Blob;
    }): Promise<void> {
        const attachmentCache: AttachmentCache = {
            userId,
            projectId,
            entityId,
            entityType,
            attachmentKey,
            attachmentData,
            cacheAt: new Date(),
        };
        
        await this.attachmentStoreService.setAttachmentCache(attachmentCache);
    }
}